import './app.css';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import React, { Suspense, useEffect, useState } from 'react';
import TagManager, { TagManagerArgs } from 'react-gtm-module';

import { ErrorBoundary } from '../components/shared/ErrorBoundary/ErrorBoundary';
import { Routes } from '../navigation/Routes';

export const App: React.FunctionComponent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const configUrl = `${process.env.REACT_APP_API_URL}/configs`;

  function hideSplashScreen() {
    const splashScreenElement = document.getElementById('splash-screen');
    if (splashScreenElement) {
      splashScreenElement.style.display = 'none';
    }
  }

  function loadGoogleMapsApiScript(key: string) {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,drawing`;
    script.async = true;
    document.head.appendChild(script);
    script.onload = () => {
      setLoading(false);
    };
    script.onerror = () => {
      setLoading(false);
      console.log('Something went wrong, google maps not loaded');
    };
  }

  function initDatadogRum(): void {
    datadogRum.init({
      applicationId: window.tz_globalConfigs.backofficeDDApplicationId,
      clientToken: window.tz_globalConfigs.backofficeDDClientToken,
      site: 'datadoghq.eu',
      service: window.tz_globalConfigs.backofficeDDService,
      env: window.tz_globalConfigs.environment,
      allowedTracingUrls: [/https:\/\/.*\.transferz\.com/],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackInteractions: true,
      trackFrustrations: true,
      defaultPrivacyLevel: 'mask-user-input',
      version: `${process.env.REACT_APP_COMMIT}`,
    } as any);

    datadogLogs.init({
      clientToken: window.tz_globalConfigs.backofficeDDClientToken,
      site: 'datadoghq.eu',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: window.tz_globalConfigs.backofficeDDService,
      env: window.tz_globalConfigs.environment,
      forwardConsoleLogs: 'all',
      version: `${process.env.REACT_APP_COMMIT}`,
    });
  }

  function initGTM(): void {
    if (window.tz_globalConfigs.backofficeGTMId) {
      const tagManagerArgs: TagManagerArgs = {
        gtmId: window.tz_globalConfigs.backofficeGTMId,
        dataLayer: {
          environment: window.tz_globalConfigs.environment,
          appVersion: `${process.env.REACT_APP_COMMIT}`,
        },
      };
      TagManager.initialize(tagManagerArgs);
    }
  }

  useEffect(() => {
    fetch(configUrl,
      { headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      } })
      .then(res => res.json())
      .then(res => {
        window['tz_globalConfigs'] = res;
        loadGoogleMapsApiScript(res.backofficePortalGoogleMapsApiKey);
        initDatadogRum();
        initGTM();
        hideSplashScreen();
      })
      .catch(e => {
        console.log(e);
        setError(e);
        setLoading(false);
        hideSplashScreen();
      });
  }, []);

  if (loading) {
    return null;
  }

  if (error) {
    return <div>An error occured while fetching environment configs.</div>;
  }

  const AppWithFeatureToggles = withLDProvider({
    clientSideID: window.tz_globalConfigs.backofficePortalLaunchDarklyId,
    options: {
      streaming: false,
    },
  })(Routes);

  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <AppWithFeatureToggles />
      </Suspense>
    </ErrorBoundary>
  );
};
